import type { ToggleButton } from '@suntex.core/animation';
import { LoadUtil, UIUtil } from '@suntex.core/animation';

import { Meter } from '../display/meter';

import { ScoreboardSymbol } from './types';

/**
 * Modern Scoreboard adapter — wraps the legacy `comsuntexsymbolsScoreboard`
 * createjs symbol with a typed, per-instance API.
 *
 * Mirrors the legacy `extendScoreboard()` behavior (see
 * `@suntex.classic/symbols/Scoreboard.ts`) but without globals.
 */
export class Scoreboard {
  private readonly symbol: ScoreboardSymbol;
  private readonly progressMeter: Meter;
  private readonly musicBtn: ToggleButton;
  private readonly muteBtn: ToggleButton;

  constructor(symbol: ScoreboardSymbol) {
    this.symbol = symbol;

    this.progressMeter = new Meter(
      symbol.progressPanel.progressMeterMc.progressMeterFill,
    );

    const percentText = symbol.progressPanel.progressMeterMc.percentCompleteTxt;
    if (percentText) {
      this.progressMeter.displayAsPercent = true;
      this.progressMeter.bindTextField(percentText);
    }

    this.musicBtn = UIUtil.createSimpleToggleButton(
      symbol.musicBtn,
      (musicOn) => this.toggleMusic(musicOn),
    );

    this.muteBtn = UIUtil.createSimpleToggleButton(symbol.muteBtn, (soundOn) =>
      this.toggleSound(soundOn),
    );

    this.updateRound();
    this.updateGame();
    this.updateBonus(null);
    this.updateCorrect();
  }

  updateRound(round?: number | null): void {
    this.updateField('round', round);
  }

  updateGame(game?: number | null): void {
    this.updateField('game', game);
  }

  updateCorrect(correct?: number | null): void {
    this.updateField('correct', correct);
  }

  updateBonus(bonus: string | number | null): void {
    if (bonus == null || bonus === '0' || bonus === 0) {
      this.updateField('bonus', null);
    } else {
      this.updateField('bonus', bonus);
    }
  }

  updateProgress(progress: number): void {
    this.progressMeter.update(progress);
  }

  showProgressPanel(): void {
    UIUtil.show(this.symbol.progressPanel, ANIM_MS);
  }

  hideProgressPanel(): void {
    UIUtil.hide(this.symbol.progressPanel);
  }

  hideMeter(): void {
    UIUtil.hide(this.symbol.progressPanel.progressMeterMc);
  }

  hideMusicButton(): void {
    UIUtil.hide(this.symbol.musicBtn);
  }

  /** Programmatically mute audio and update the mute button visual state. */
  mute(): void {
    this.toggleSound(false);
    this.muteBtn.toggleOff();
  }

  private toggleMusic(musicOn: boolean): void {
    if (musicOn) {
      LoadUtil.restartLoop();
    } else {
      LoadUtil.stopLoop();
    }
  }

  private toggleSound(soundOn: boolean): void {
    if (soundOn) {
      LoadUtil.unmute();
      UIUtil.enable(this.musicBtn);
      UIUtil.fadeTo(this.musicBtn, 1.0);
      if (this.musicBtn.value) UIUtil.setFrame(this.musicBtn, 1);
    } else {
      LoadUtil.mute();
      UIUtil.disable(this.musicBtn);
      UIUtil.setFrame(this.musicBtn, 3);
    }
  }

  private updateField(name: string, value: string | number | null | undefined) {
    const panel = this.symbol.progressPanel as createjs.Container;
    const label = findChild(panel, name + 'Lbl');
    const textField = findChild(panel, name + 'Txt') as createjs.Text | null;

    if (value == null) {
      if (label) UIUtil.hide(label);
      if (textField) textField.text = '';
    } else {
      if (label) UIUtil.show(label);
      if (textField) textField.text = String(value);
    }
  }
}

const ANIM_MS = 500;

/**
 * Recursively find a named child within a createjs Container. Replaces the
 * legacy `UIUtil.find(parent, name)` helper.
 */
function findChild(
  container: createjs.Container,
  name: string,
): createjs.DisplayObject | null {
  for (const child of container.children) {
    if (child.name === name) return child;
    const childContainer = child as createjs.Container;
    if (Array.isArray(childContainer.children)) {
      const found = findChild(childContainer, name);
      if (found) return found;
    }
  }
  return null;
}
